import { cn } from '@/utils/cn'

interface PaginationProps {
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
}

export function Pagination({
  total, page, pageSize, onPageChange, onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  if (totalPages <= 1 && !onPageSizeChange) return null

  const visiblePages: number[] = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) visiblePages.push(i)
  } else {
    visiblePages.push(1)
    if (page > 3) visiblePages.push(-1)
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      visiblePages.push(i)
    }
    if (page < totalPages - 2) visiblePages.push(-2)
    visiblePages.push(totalPages)
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span>
          {total > 0
            ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} von ${total.toLocaleString('de-CH')}`
            : 'Keine Ergebnisse'}
        </span>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 border rounded text-sm"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size} pro Seite</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1 border rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          ←
        </button>
        {visiblePages.map((p, i) =>
          p < 0 ? (
            <span key={`ellipsis-${i}`} className="px-2 py-1 text-gray-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={cn(
                'px-3 py-1 border rounded text-sm',
                p === page ? 'bg-primary-600 text-white border-primary-600' : 'hover:bg-gray-50'
              )}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="px-3 py-1 border rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          →
        </button>
      </div>
    </div>
  )
}
